/**
 * Client-side encrypted password vault (zero-knowledge).
 * The master password never leaves the browser and is never stored. It is stretched with
 * PBKDF2-SHA256 into an AES-256-GCM key; every entry (including its title) is encrypted
 * separately with a fresh random IV. The database only ever sees { iv, ct } blobs.
 */
const ITERATIONS = 600000 // OWASP 2023 recommendation for PBKDF2-SHA256
const CHECK = 'money-vault-v1'
const enc = new TextEncoder()
const dec = new TextDecoder()

const toB64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)))
const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0))
const random = (n) => crypto.getRandomValues(new Uint8Array(n))

export async function deriveKey(password, kdf) {
  const base = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: fromB64(kdf.salt), iterations: kdf.iterations, hash: 'SHA-256' },
    base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'],
  )
}

export async function encrypt(key, value) {
  const iv = random(12)
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(value)))
  return { iv: toB64(iv), ct: toB64(ct) }
}

export async function decrypt(key, blob) {
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromB64(blob.iv) }, key, fromB64(blob.ct))
  return JSON.parse(dec.decode(pt))
}

/** New vault header: KDF params + an encrypted known value to verify the master password. */
export async function createVault(password) {
  const kdf = { salt: toB64(random(16)), iterations: ITERATIONS, hash: 'SHA-256' }
  const key = await deriveKey(password, kdf)
  return { key, header: { kdf, check: await encrypt(key, CHECK), createdAt: Date.now() } }
}

/** Returns the key, or null if the master password is wrong. */
export async function unlockVault(password, header) {
  const key = await deriveKey(password, header.kdf)
  try {
    return (await decrypt(key, header.check)) === CHECK ? key : null
  } catch {
    return null // AES-GCM authentication fails on a wrong key
  }
}

export function generatePassword(length = 20) {
  const sets = ['abcdefghijkmnopqrstuvwxyz', 'ABCDEFGHJKLMNPQRSTUVWXYZ', '23456789', '!@#$%^&*-_=+?']
  const all = sets.join('')
  const pick = (chars) => {
    // rejection sampling → no modulo bias
    const limit = 256 - (256 % chars.length)
    for (;;) { const [b] = random(1); if (b < limit) return chars[b % chars.length] }
  }
  const out = sets.map(pick)
  while (out.length < length) out.push(pick(all))
  for (let i = out.length - 1; i > 0; i--) { const j = Math.floor((random(1)[0] / 256) * (i + 1)); [out[i], out[j]] = [out[j], out[i]] }
  return out.join('')
}

/** Rough strength score 0–4 for the master password hint. */
export function strength(pw) {
  let s = 0
  if (pw.length >= 12) s++
  if (pw.length >= 16) s++
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s++
  return s
}
